import { el, clear, showToast, showConfirm, showPrompt, t } from './dom';
import { api, ApiError } from './api';
import type { User, UserRole } from './types';
import { buildLanguageSelector } from './languageSelector';
import { COMPANY } from './config/company';
import { i18n } from './i18n/i18n';

interface AdminUser {
  id: string;
  email: string;
  role: UserRole;
  isActive: boolean;
  createdAt: string;
  lastLoginAt: string | null;
}

export async function renderUserManagement(
  root: HTMLElement,
  user: User,
  onBack: () => void,
) {
  clear(root);

  const header = el('header', { className: 'header' });

  const brand = el('div', { className: 'header-brand' });
  const logoImg = document.createElement('img');
  logoImg.src = COMPANY.logoUrl;
  logoImg.alt = COMPANY.name;
  logoImg.className = 'header-logo';
  brand.appendChild(logoImg);
  brand.appendChild(el('h1', { className: 'header-title', text: t('users.title') }));

  header.append(
    el('button', {
      className: 'btn-logout',
      text: t('common.back'),
      onClick: onBack,
    }),
    brand,
    el('div', { className: 'user-info' }, [
      buildLanguageSelector(() => renderUserManagement(root, user, onBack)),
    ]),
  );

  const main = el('main', { className: 'main' });
  const content = el('div');
  main.append(content);
  root.append(header, main);

  let users: AdminUser[] = [];

  const loadAll = async () => {
    clear(content);
    content.appendChild(el('p', { text: t('common.loading'), className: 'loading' }));
    try {
      users = await api.request<AdminUser[]>('GET', '/admin/users');
      render();
    } catch (e) {
      clear(content);
      const msg = e instanceof ApiError ? e.message : t('common.networkError');
      content.appendChild(el('div', { className: 'empty-state', text: msg }));
    }
  };

  const render = () => {
    clear(content);

    // Toolbar
    const toolbar = el('div', { className: 'toolbar' });
    toolbar.appendChild(el('button', {
      className: 'btn btn-primary',
      text: t('users.newUser'),
      onClick: () => openUserForm(null),
    }));
    content.appendChild(toolbar);

    // Tabla de usuarios
    const card = el('div', { className: 'card' });
    const table = el('table', { className: 'items-table' });
    const thead = el('thead');
    const headRow = el('tr');
    [
      t('users.email'),
      t('users.role'),
      t('users.status'),
      t('users.lastLogin'),
      t('users.actions'),
    ].forEach(text => {
      headRow.appendChild(el('th', { text }));
    });
    thead.appendChild(headRow);
    table.appendChild(thead);

    const tbody = el('tbody');
    for (const u of users) {
      const tr = el('tr');
      if (!u.isActive) tr.style.opacity = '0.5';

      const lastLogin = u.lastLoginAt ? i18n.formatDate(u.lastLoginAt) : t('users.never');
      const roleLabel = u.role === 'ADMIN' ? t('users.roleAdmin') : t('users.roleEmployee');

      tr.append(
        el('td', { text: u.email }),
        el('td', { text: roleLabel }),
        el('td', { text: u.isActive ? t('users.statusActive') : t('users.statusInactive') }),
        el('td', { text: lastLogin }),
      );

      const actions = el('td');

      // Editar (no permitir editar al usuario actual)
      actions.appendChild(el('button', {
        className: 'btn btn-secondary',
        text: t('common.edit'),
        onClick: () => openUserForm(u),
      }));

      // Resetear contraseña
      actions.appendChild(el('button', {
        className: 'btn btn-secondary',
        text: t('users.resetPassword'),
        onClick: async () => {
          const newPwd = await showPrompt(
            t('users.resetPasswordPrompt', { email: u.email }),
            { required: true, minLength: 8 },
          );
          if (!newPwd) return;
          try {
            await api.request('POST', `/admin/users/${u.id}/reset-password`, { newPassword: newPwd });
            showToast(t('users.passwordReset'), 'success');
          } catch (e) {
            const msg = e instanceof ApiError ? e.message : t('common.unknownError');
            showToast(msg, 'error');
          }
        },
      }));

      // Activar/desactivar (no permitir auto-desactivación)
      if (u.id !== user.id) {
        if (u.isActive) {
          actions.appendChild(el('button', {
            className: 'btn btn-danger',
            text: t('users.deactivate'),
            onClick: async () => {
              if (!await showConfirm(t('users.deactivateConfirm', { email: u.email }))) return;
              try {
                await api.request('PATCH', `/admin/users/${u.id}`, { isActive: false });
                showToast(t('users.deactivated'), 'success');
                await loadAll();
              } catch (e) {
                const msg = e instanceof ApiError ? e.message : t('common.unknownError');
                showToast(msg, 'error');
              }
            },
          }));
        } else {
          actions.appendChild(el('button', {
            className: 'btn btn-success',
            text: t('users.activate'),
            onClick: async () => {
              if (!await showConfirm(t('users.activateConfirm', { email: u.email }))) return;
              try {
                await api.request('PATCH', `/admin/users/${u.id}`, { isActive: true });
                showToast(t('users.activated'), 'success');
                await loadAll();
              } catch (e) {
                const msg = e instanceof ApiError ? e.message : t('common.unknownError');
                showToast(msg, 'error');
              }
            },
          }));
        }
      }

      tr.appendChild(actions);
      tbody.appendChild(tr);
    }
    table.appendChild(tbody);
    card.appendChild(table);
    content.appendChild(card);
  };

  // Modal: crear/editar usuario
  const openUserForm = (existing: AdminUser | null) => {
    const backdrop = el('div', { className: 'modal-backdrop' });
    const modal = el('div', { className: 'modal' });
    modal.appendChild(el('h3', {
      text: existing ? t('users.formTitleEdit') : t('users.formTitleNew'),
    }));

    // Email
    const emailGroup = el('div', { className: 'form-group' });
    emailGroup.appendChild(el('label', { text: t('users.email') }));
    const emailInput = document.createElement('input');
    emailInput.type = 'email';
    emailInput.maxLength = 255;
    emailInput.value = existing?.email ?? '';
    emailGroup.appendChild(emailInput);

    // Password (solo al crear)
    let passwordInput: HTMLInputElement | null = null;
    if (!existing) {
      const pwdGroup = el('div', { className: 'form-group' });
      pwdGroup.appendChild(el('label', { text: t('users.password') }));
      passwordInput = document.createElement('input');
      passwordInput.type = 'password';
      passwordInput.maxLength = 128;
      passwordInput.autocomplete = 'new-password';
      pwdGroup.appendChild(passwordInput);
      modal.appendChild(emailGroup);
      modal.appendChild(pwdGroup);
    } else {
      modal.appendChild(emailGroup);
    }

    // Rol
    const roleGroup = el('div', { className: 'form-group' });
    roleGroup.appendChild(el('label', { text: t('users.role') }));
    const roleSelect = document.createElement('select');
    [
      { value: 'EMPLOYEE', label: t('users.roleEmployee') },
      { value: 'ADMIN', label: t('users.roleAdmin') },
    ].forEach(r => {
      const opt = document.createElement('option');
      opt.value = r.value;
      opt.textContent = r.label;
      if (existing?.role === r.value) opt.selected = true;
      roleSelect.appendChild(opt);
    });
    // No permitir cambiar el rol del usuario actual
    if (existing && existing.id === user.id) {
      roleSelect.disabled = true;
    }
    roleGroup.appendChild(roleSelect);
    modal.appendChild(roleGroup);

    const actions = el('div', { className: 'modal-actions' });
    actions.appendChild(el('button', {
      className: 'btn btn-secondary',
      text: t('common.cancel'),
      onClick: () => backdrop.remove(),
    }));
    const saveBtn = el('button', {
      className: 'btn btn-primary',
      text: t('common.save'),
      onClick: async () => {
        const email = emailInput.value.trim();
        if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
          showToast(t('common.requiredField'), 'error');
          return;
        }
        if (!existing && passwordInput) {
          if (!passwordInput.value || passwordInput.value.length < 8) {
            showToast(t('account.passwordMin'), 'error');
            return;
          }
        }

        saveBtn.disabled = true;
        try {
          if (existing) {
            const data: any = { email };
            if (!roleSelect.disabled) data.role = roleSelect.value;
            await api.request('PATCH', `/admin/users/${existing.id}`, data);
            showToast(t('users.updated'), 'success');
          } else {
            await api.request('POST', '/admin/users', {
              email,
              password: passwordInput!.value,
              role: roleSelect.value,
            });
            showToast(t('users.created'), 'success');
          }
          backdrop.remove();
          await loadAll();
        } catch (e) {
          const msg = e instanceof ApiError ? e.message : t('common.unknownError');
          showToast(msg, 'error');
          saveBtn.disabled = false;
        }
      },
    });
    actions.appendChild(saveBtn);
    modal.appendChild(actions);

    backdrop.appendChild(modal);
    document.body.appendChild(backdrop);
    setTimeout(() => emailInput.focus(), 50);
  };

  await loadAll();
}
